import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP } from "../lib/format";
import { toast } from "sonner";
import { Plus, ChevronRight, ChevronDown, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const TIPO_LABELS = {
  activo: "Activo",
  pasivo: "Pasivo",
  patrimonio: "Patrimonio",
  ingreso: "Ingreso",
  egreso: "Egreso",
};

const TIPO_COLORS = {
  activo: "bg-[#1B4332]/10 text-[#1B4332]",
  pasivo: "bg-[#A94438]/10 text-[#A94438]",
  patrimonio: "bg-[#D4A373]/20 text-[#5C3F1F]",
  ingreso: "bg-[#2D6A4F]/10 text-[#2D6A4F]",
  egreso: "bg-[#59645E]/10 text-[#59645E]",
};

export default function PlanCuentas() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    codigo: "",
    nombre: "",
    tipo: "activo",
    parent_id: "",
    imputable: true,
  });

  const load = async () => {
    try {
      const { data } = await api.get("/cuentas");
      setCuentas(data);
      // Expand top level by default
      setExpanded(new Set(data.filter((c) => !c.parent_id).map((c) => c.id)));
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const childrenOf = (parentId) =>
    cuentas.filter((c) => c.parent_id === parentId).sort((a, b) => a.codigo.localeCompare(b.codigo));

  const toggle = (id) => {
    const s = new Set(expanded);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpanded(s);
  };

  const renderRow = (c, depth = 0) => {
    const hijos = childrenOf(c.id);
    const isOpen = expanded.has(c.id);
    return (
      <React.Fragment key={c.id}>
        <tr className="hover:bg-[#F9F8F6]" data-testid={`cuenta-row-${c.codigo}`}>
          <td>
            <div className="flex items-center gap-1" style={{ paddingLeft: depth * 18 }}>
              {hijos.length > 0 ? (
                <button onClick={() => toggle(c.id)} className="text-[#59645E] hover:text-[#1B4332]">
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              ) : (
                <span className="w-3.5" />
              )}
              <span className="num text-[#1C211F]">{c.codigo}</span>
            </div>
          </td>
          <td>
            <span className={depth === 0 ? "font-semibold text-[#1C211F]" : "text-[#1C211F]"}>{c.nombre}</span>
          </td>
          <td>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase font-semibold ${TIPO_COLORS[c.tipo]}`}>
              {TIPO_LABELS[c.tipo]}
            </span>
          </td>
          <td>
            <span className="text-[11px] text-[#59645E]">
              {c.imputable ? "Imputable" : "Agrupadora"}
            </span>
          </td>
          <td className="num">{fmtCLP(c.saldo_inicial)}</td>
          <td>
            <button
              onClick={() => handleDelete(c)}
              data-testid={`delete-cuenta-${c.codigo}`}
              className="text-[#A94438] hover:bg-[#A94438]/10 p-1 rounded-sm"
              title="Eliminar"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </td>
        </tr>
        {isOpen && hijos.map((h) => renderRow(h, depth + 1))}
      </React.Fragment>
    );
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar cuenta ${c.codigo} - ${c.nombre}?`)) return;
    try {
      await api.delete(`/cuentas/${c.id}`);
      toast.success("Cuenta eliminada");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cuentas", {
        ...form,
        parent_id: form.parent_id || null,
        saldo_inicial: 0,
      });
      toast.success("Cuenta creada");
      setOpenDialog(false);
      setForm({ codigo: "", nombre: "", tipo: "activo", parent_id: "", imputable: true });
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const roots = childrenOf(null);

  return (
    <Page>
      <PageHeader
        title="Plan de cuentas"
        subtitle="Árbol contable jerárquico"
        testid="plan-cuentas-header"
        actions={
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <button
                data-testid="new-cuenta-button"
                className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-3 py-2 rounded-sm transition-colors"
              >
                <Plus className="h-4 w-4" /> Nueva cuenta
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nueva cuenta contable</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="new-cuenta-form">
                <div>
                  <label className="label-caps block mb-1">Código</label>
                  <input
                    required
                    data-testid="cuenta-codigo"
                    value={form.codigo}
                    onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                    placeholder="ej: 1.1.05"
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/30 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Nombre</label>
                  <input
                    required
                    data-testid="cuenta-nombre"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/30 text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Tipo</label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="cuenta-tipo" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIPO_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-caps block mb-1">Cuenta padre (opcional)</label>
                  <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="cuenta-parent" className="text-sm">
                      <SelectValue placeholder="Ninguna" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Ninguna —</SelectItem>
                      {cuentas
                        .filter((c) => !c.imputable && c.tipo === form.tipo)
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.codigo} · {c.nombre}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="imputable"
                    checked={form.imputable}
                    onChange={(e) => setForm({ ...form, imputable: e.target.checked })}
                    data-testid="cuenta-imputable"
                  />
                  <label htmlFor="imputable" className="text-sm text-[#1C211F]">
                    Imputable (acepta movimientos)
                  </label>
                </div>
                <DialogFooter>
                  <button
                    type="submit"
                    data-testid="submit-cuenta"
                    className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-4 py-2 rounded-sm"
                  >
                    Crear cuenta
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto" data-testid="cuentas-table-container">
        {loading ? (
          <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
        ) : cuentas.length === 0 ? (
          <div className="p-8 text-center text-[#59645E] text-sm">No hay cuentas. Crea la primera.</div>
        ) : (
          <table className="acc-table" data-testid="cuentas-table">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Código</th>
                <th>Nombre</th>
                <th style={{ width: 110 }}>Tipo</th>
                <th style={{ width: 110 }}>Estado</th>
                <th className="num" style={{ width: 140 }}>Saldo inicial</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>{roots.map((r) => renderRow(r, 0))}</tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
